import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { Observable, filter, map, of, take, timeout } from "rxjs";
import { PermissionService } from "../../../services/permission.service";
import { MenuService } from "../../../services/menu.service";
import { PermissionConstants } from "../../../constants/permission-constants";
import { DASHBOARDS_URL, firstPermittedRoute } from "../utils/home-route.utils";

/** How long Home waits for the sidebar menu (fetched right after login) before giving up. */
export const MENU_WAIT_MS = 4000;

/** The first page the user's menu allows (Home skipped), once the menu is there; null if none / timed out. */
export function firstPermittedPage(menuService: MenuService): Observable<string | null> {
  return menuService.getMenuItems().pipe(
    filter(items => (items?.length ?? 0) > 0),
    take(1),
    timeout({ first: MENU_WAIT_MS, with: () => of([]) }),
    map(items => firstPermittedRoute(items))
  );
}

/**
 * Home without NAV_HOME → the first page the user's menu allows (login lands on Home). If there is none, the route's
 * `requiresPermission` makes the layout show the No-access page.
 */
export const homeGuard: CanActivateFn = () => {
  const permissions = inject(PermissionService);
  if (permissions.evaluatePermissionsForLoggedInUser({ permissionIds: [PermissionConstants.HOME.NAV] })) return true;
  const router = inject(Router);
  return firstPermittedPage(inject(MenuService)).pipe(map(route => (route ? router.parseUrl(route) : true)));
};

/**
 * Create / edit need the route's `requiresPermission` (CREATE_DASHBOARD / EDIT_DASHBOARD): without it, edit/:id goes
 * to view/:id and create to the Dashboards list.
 */
export const dashboardPermissionGuard: CanActivateFn = (route) => {
  if (inject(PermissionService).evaluatePermissionsForLoggedInUser(route.data["requiresPermission"])) return true;
  const id = route.paramMap.get("id");
  return inject(Router).createUrlTree(id ? [DASHBOARDS_URL, "view", id] : [DASHBOARDS_URL]);
};
